import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { analyticsApi } from '@/lib/api';
import { useLanguage } from '@/context/LanguageContext';
import { 
  BarChart3, TrendingUp, Calendar, Flame, 
  Smile, Meh, Frown, Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line
} from 'recharts';

const COLORS = ['#22c55e', '#ef4444', '#94a3b8'];

export default function AnalyticsPage() {
  const { t } = useLanguage();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const response = await analyticsApi.get();
        setAnalytics(response.data);
      } catch (error) {
        console.error('Failed to fetch analytics:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="animate-spin text-cyan" size={32} />
      </div>
    );
  }

  const sentimentData = analytics ? [
    { name: 'Positive', value: analytics.sentiment_distribution.positive },
    { name: 'Negative', value: analytics.sentiment_distribution.negative },
    { name: 'Neutral', value: analytics.sentiment_distribution.neutral },
  ] : [];

  const moodTrendData = analytics?.mood_trend?.slice(0, 14).reverse().map(item => ({
    date: item.date.slice(5), // MM-DD format
    mood: item.mood
  })) || [];

  return (
    <div className="space-y-8" data-testid="analytics-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl text-white mb-2 flex items-center gap-3">
          <BarChart3 className="text-cyan" />
          Analytics
        </h1>
        <p className="text-slate-400">{t('analytics_subtitle')}</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0 }}>
          <Card className="bg-navy-900/40 border-white/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Smile className="text-cyan" size={18} />
                <span className="text-slate-400 text-sm">{t('analytics_avg_mood')}</span>
              </div>
              <p className="text-4xl font-heading text-white">{analytics?.average_mood || '—'}</p>
              <p className="text-xs text-slate-500 mt-1">{t('analytics_out_of')}</p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card className="bg-navy-900/40 border-white/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="text-cyan" size={18} />
                <span className="text-slate-400 text-sm">{t('analytics_total')}</span>
              </div>
              <p className="text-4xl font-heading text-white">{analytics?.total_notes || 0}</p>
              <p className="text-xs text-slate-500 mt-1">{t('analytics_entries')}</p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <Card className="bg-navy-900/40 border-white/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Flame className="text-gold" size={18} />
                <span className="text-slate-400 text-sm">{t('analytics_streak')}</span>
              </div>
              <p className="text-4xl font-heading text-white">{analytics?.streak_days || 0}</p>
              <p className="text-xs text-slate-500 mt-1">{t('analytics_days')}</p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <Card className="bg-navy-900/40 border-white/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className={analytics?.improvement_percentage >= 0 ? 'text-green-400' : 'text-red-400'} size={18} />
                <span className="text-slate-400 text-sm">{t('analytics_progress')}</span>
              </div>
              <p className="text-4xl font-heading text-white">
                {analytics?.improvement_percentage >= 0 ? '+' : ''}{analytics?.improvement_percentage || 0}%
              </p>
              <p className="text-xs text-slate-500 mt-1">{t('analytics_vs_week')}</p>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Mood Trend Chart */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
          <Card className="bg-navy-900/40 border-white/5">
            <CardHeader>
              <CardTitle className="text-white text-lg">Mood Trend</CardTitle>
            </CardHeader>
            <CardContent>
              {moodTrendData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={moodTrendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e4060" />
                    <XAxis dataKey="date" stroke="#94a3b8" fontSize={12} />
                    <YAxis domain={[0, 10]} stroke="#94a3b8" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#0f253a', 
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '8px'
                      }}
                      labelStyle={{ color: '#e2e8f0' }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="mood" 
                      stroke="#00d4ff" 
                      strokeWidth={2}
                      dot={{ fill: '#00d4ff', r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[250px] flex items-center justify-center text-slate-500">
                  No mood data yet
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Sentiment Distribution */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
          <Card className="bg-navy-900/40 border-white/5">
            <CardHeader>
              <CardTitle className="text-white text-lg">Sentiment Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              {sentimentData.some(d => d.value > 0) ? (
                <div className="flex items-center">
                  <ResponsiveContainer width="60%" height={200}>
                    <PieChart>
                      <Pie
                        data={sentimentData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {sentimentData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#0f253a', 
                          border: '1px solid rgba(255,255,255,0.1)',
                          borderRadius: '8px'
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex-1 space-y-3">
                    {sentimentData.map((item, index) => (
                      <div key={item.name} className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index] }} />
                        <span className="text-slate-300 text-sm">{item.name}</span>
                        <span className="text-slate-500 text-sm ml-auto">{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="h-[200px] flex items-center justify-center text-slate-500">
                  No sentiment data yet
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Top Tags */}
      {analytics?.top_tags?.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}>
          <Card className="bg-navy-900/40 border-white/5">
            <CardHeader>
              <CardTitle className="text-white text-lg">Top Topics</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={analytics.top_tags} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e4060" />
                  <XAxis type="number" stroke="#94a3b8" fontSize={12} />
                  <YAxis dataKey="tag" type="category" stroke="#94a3b8" fontSize={12} width={80} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#0f253a', 
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="count" fill="#d4a03c" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </div>
  );
}
